<!--
  Modo predictivo de anomalías con IA (reporte r116).

  Un renglón por variable de sensor con su estado, su tendencia y la fecha
  estimada del PRÓXIMO evento de desviación, antes de que ocurra. Este
  componente no calcula nada: el análisis vive en PredictivoAnomaliasService
  (backend) y aquí solo se pinta lo que devuelve /api/PredictivoAnomalias.
  Cada instancia muestra su propia planta.

  Botones por renglón:
    · Pronóstico — histórico diario + proyección por tendencia (gráfica).
    · Hoy        — comportamiento del día en curso por minuto (gráfica).
    · Rangos     — rango automático (IA) vs rango manual (plataforma), con el
                   selector de modo AUTO/MANUAL por variable.

  En AUTO el renglón se evalúa con el rango calculado por IA a partir del
  comportamiento reciente; en MANUAL con el rango capturado en la plataforma
  (Configuración → Máquinas y herramientas → "Configurar rangos").
-->
<template>
  <v-col cols="12" class="pa-0">
    <div id="predictivo-anomalias">
      <!-- Encabezado del reporte: título, hora del último cálculo y refresco manual. -->
      <div class="pa-head">
        <div>
          <h1>Modo predictivo de anomalías con IA</h1>
          <div class="sub">
            Un renglón por variable · actualizado {{ val(tablero, 'actualizado', 'Actualizado') || '—' }}
          </div>
        </div>
        <div class="der">
          <span v-if="enlaceTexto" :class="['enlace', enlaceVivo ? 'vivo' : 'caido']">{{ enlaceTexto }}</span>
          <span v-if="enDesviacion !== null" class="kpi">{{ enDesviacion }} en desviación</span>
          <button class="claro" :disabled="cargando" @click="refrescar">Actualizar ahora</button>
        </div>
      </div>

      <main>
        <div v-if="error" class="error-msg">{{ error }}</div>

        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="th">Estado</th>
                <th class="th">Máquina · sensor · variable</th>
                <th class="th">Última lectura</th>
                <th class="th">Tendencia</th>
                <th class="th">Próxima desviación estimada</th>
                <th class="th centrado">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="f in filas">
                <!-- Renglón principal de la variable. -->
                <tr :key="idDe(f)">
                  <td>
                    <v-chip small dark :color="colorEstado(estadoDe(f))">{{ etiquetaEstado(estadoDe(f)) }}</v-chip>
                  </td>
                  <td>
                    <div class="fw">{{ val(f, 'maquina', 'Maquina') }}</div>
                    <div class="mini">
                      {{ val(f, 'sensor', 'Sensor') }} · {{ val(f, 'variable', 'Variable') }}
                      <span class="mono">({{ val(f, 'clave', 'Clave') }})</span>
                    </div>
                  </td>
                  <td>
                    <div class="fw" :style="{ color: colorValor(estadoDe(f)) }">
                      {{ val(f, 'ultimoValor', 'UltimoValor') != null ? val(f, 'ultimoValor', 'UltimoValor') + unidadDe(f) : '—' }}
                    </div>
                    <div class="mini">
                      hace {{ val(f, 'minutosSinDato', 'MinutosSinDato') != null ? val(f, 'minutosSinDato', 'MinutosSinDato') + ' min' : '—' }}
                    </div>
                  </td>
                  <td>
                    <span v-if="tendenciaDe(f) != null" :style="{ color: tendenciaDe(f) > 0 ? '#b26a00' : '#1976d2' }">
                      {{ tendenciaDe(f) > 0 ? '▲ +' : '▼ ' }}{{ tendenciaDe(f) }}{{ unidadDe(f) }}/día
                    </span>
                    <span v-else class="mini">—</span>
                  </td>
                  <td>
                    <div class="fw">{{ val(f, 'proximoEvento', 'ProximoEvento') }}</div>
                    <div class="mini">{{ val(f, 'proximoEventoDetalle', 'ProximoEventoDetalle') }}</div>
                  </td>
                  <td class="centrado acciones">
                    <button class="accion verde" @click="abrirSerie(f, 'pronostico')">Pronóstico</button>
                    <button class="accion azul" @click="abrirSerie(f, 'hoy')">Hoy</button>
                    <button :class="['accion', 'ambar', modoDe(f) === 'MANUAL' ? 'solido' : '']" @click="abrirRangos(f)">Rangos</button>
                  </td>
                </tr>

                <!-- Panel expandible del renglón: gráfica o tarjetas de rangos. -->
                <tr v-if="abierto.id === idDe(f)" :key="idDe(f) + '-panel'" class="panel">
                  <td colspan="6">
                    <!-- Panel de rangos: automático (IA) vs manual (plataforma) + modo. -->
                    <div v-if="abierto.tipo === 'rangos'" class="pa-panel">
                      <div class="panel-head">
                        <strong>Rangos de {{ val(f, 'variable', 'Variable') }} ({{ val(f, 'clave', 'Clave') }}) — {{ val(f, 'maquina', 'Maquina') }}</strong>
                        <button class="claro" @click="cerrarPanel">✕ Cerrar</button>
                      </div>
                      <div class="rangos">
                        <div :class="['tarjeta', modoDe(f) !== 'MANUAL' ? 'activa' : '']">
                          <div class="tarjeta-head">
                            <b>Automático — sugerido por IA</b>
                            <span v-if="modoDe(f) !== 'MANUAL'" class="enuso">EN USO</span>
                          </div>
                          <div v-if="rangoAutoDe(f)">
                            <div>Normal: <b class="mono">{{ textoRango(rangoAutoDe(f), 'normal') }}</b></div>
                            <div>Advertencia: <b class="mono">{{ textoRango(rangoAutoDe(f), 'advertencia') }}</b> <span class="mini">(más allá = alarma)</span></div>
                            <div class="mini nota">Congelado el {{ val(rangoAutoDe(f), 'fechaCalculo', 'FechaCalculo') || '—' }}. Se recalcula sólo con el botón, usando los últimos 15 días.</div>
                          </div>
                          <div v-else class="mini">Recolectando historial para calcularlo (se necesita al menos un día de lecturas).</div>
                          <div class="modo-barra">
                            <button class="accion verde" :disabled="recalculando" @click="recalcular(f)">
                              {{ recalculando ? 'recalculando…' : (confirmandoRecalculo ? 'Confirmar recálculo' : 'Recalcular') }}
                            </button>
                            <span v-if="confirmandoRecalculo && !recalculando" class="mini">
                              Vuelve a pulsar para confirmar: el rango en uso se reemplaza con los últimos 15 días.
                            </span>
                          </div>
                        </div>
                        <div :class="['tarjeta', modoDe(f) === 'MANUAL' ? 'activa' : '']">
                          <div class="tarjeta-head">
                            <b>Manual — capturado en la plataforma</b>
                            <span v-if="modoDe(f) === 'MANUAL'" class="enuso ambar-bg">EN USO</span>
                          </div>
                          <div v-if="rangoManualDe(f)">
                            <div>Normal: <b class="mono">{{ textoRango(rangoManualDe(f), 'normal') }}</b></div>
                            <div>Advertencia: <b class="mono">{{ textoRango(rangoManualDe(f), 'advertencia') }}</b> <span class="mini">(más allá = alarma)</span></div>
                          </div>
                          <div v-else class="mini ambar-txt">Sin rango capturado en la plataforma.</div>
                          <div class="mini nota">Se edita en la plataforma: Configuración → Máquinas y herramientas → (máquina) → "Configurar rangos".</div>
                        </div>
                      </div>
                      <div class="modo-barra">
                        <strong>Modo de este renglón:</strong>
                        <label><input type="radio" value="AUTO" v-model="modoSel"> Automático (IA)</label>
                        <label><input type="radio" value="MANUAL" v-model="modoSel"> Manual (plataforma)</label>
                        <button class="accion verde solido" :disabled="guardandoModo" @click="guardarModo(f)">
                          {{ guardandoModo ? 'guardando…' : 'Aplicar' }}
                        </button>
                        <span v-if="modoGuardado" class="ok">Guardado ✓</span>
                      </div>
                      <div class="mini nota">
                        En automático el renglón se evalúa y pronostica con el rango calculado por IA; en manual, con el
                        capturado en la plataforma. Solo cambia este reporte, no las alertas de la plataforma.
                      </div>
                    </div>

                    <!-- Panel de gráfica: pronóstico (histórico + proyección) u hoy (por minuto). -->
                    <div v-else class="pa-panel">
                      <div class="panel-head">
                        <strong>
                          {{ abierto.tipo === 'hoy' ? 'Comportamiento de hoy (por minuto)' : 'Histórico diario y proyección' }}
                          — {{ val(f, 'variable', 'Variable') }} ({{ val(f, 'clave', 'Clave') }})
                        </strong>
                        <button class="claro" @click="cerrarPanel">✕ Cerrar gráfica</button>
                      </div>
                      <div v-if="cargandoSerie" class="mini centrado" style="padding:24px">cargando gráfica…</div>
                      <apexchart v-else :key="graficaKey" type="line" height="320" :options="chartOptions" :series="chartSeries" />
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </template>
        </v-simple-table>

        <div class="nota mini">
          Pronóstico estadístico (tendencia robusta + ciclo diario) calculado en el backend de la propia planta;
          se recalcula con cada dato nuevo y las fechas se ajustan conforme evoluciona la tendencia.
        </div>
      </main>
    </div>
  </v-col>
</template>

<script>
import axios from "axios";
import VueApexCharts from "vue-apexcharts";

export default {
  name: "ReportePredictivoAnomalias",
  components: { apexchart: VueApexCharts },
  props: {
    /** El dashboard marca active cuando esta pestaña es la visible. */
    active: { type: Boolean, default: true },
  },
  data() {
    return {
      /** Respuesta completa de /PredictivoAnomalias/Estado. */
      tablero: {},
      /** Mensaje de error visible cuando el backend no responde. */
      error: "",
      /** true mientras hay una consulta del tablero en curso. */
      cargando: false,
      /** Panel abierto: id de la variable y tipo ('pronostico' | 'hoy' | 'rangos'). */
      abierto: { id: null, tipo: null },
      /** true mientras se descarga la serie de una gráfica. */
      cargandoSerie: false,
      /** Opciones y series de la gráfica abierta (ApexCharts). */
      chartOptions: {},
      chartSeries: [],
      /** Cambia para forzar el re-render de la gráfica al abrir otra. */
      graficaKey: 0,
      /** Modo elegido en el panel de rangos antes de Aplicar. */
      modoSel: "AUTO",
      guardandoModo: false,
      modoGuardado: false,
      /** true tras el primer clic en "Recalcular": el segundo clic confirma. */
      confirmandoRecalculo: false,
      /** true mientras el backend recalcula el rango automático. */
      recalculando: false,
      /** Timer del refresco automático (cada minuto con la pestaña activa). */
      timer: null,
    };
  },
  computed: {
    /** Renglones del tablero tolerando ambas capitalizaciones del API. */
    filas() {
      return this.val(this.tablero, "filas", "Filas") || [];
    },
    /** Cuántas variables están fuera de su rango en este momento. */
    enDesviacion() {
      const v = this.val(this.tablero, "enDesviacion", "EnDesviacion");
      return v === undefined ? null : v;
    },
    /** true si la última lectura de la planta es de hace pocos minutos. */
    enlaceVivo() {
      return !!this.val(this.tablero, "enlaceVivo", "EnlaceVivo");
    },
    /** Texto del chip del enlace ("hace N min · vivo/caído"). */
    enlaceTexto() {
      const m = this.val(this.tablero, "enlaceMinutosSinDato", "EnlaceMinutosSinDato");
      if (m === null || m === undefined) return "";
      return "enlace: hace " + m + " min · " + (this.enlaceVivo ? "vivo" : "caído");
    },
  },
  methods: {
    /**
     * Lee un campo tolerando las dos capitalizaciones que entrega el API
     * (Newtonsoft serializa en PascalCase; algunos proxys lo pasan a camelCase).
     */
    val(objeto, campo, Campo) {
      if (!objeto) return undefined;
      return objeto[campo] !== undefined ? objeto[campo] : objeto[Campo];
    },

    /** Id de la variable del renglón (llave para paneles y series). */
    idDe(f) {
      return this.val(f, "idMaquinaSensorVariable", "IdMaquinaSensorVariable");
    },
    estadoDe(f) {
      return this.val(f, "estado", "Estado");
    },
    modoDe(f) {
      return this.val(f, "modo", "Modo") || "AUTO";
    },
    tendenciaDe(f) {
      const t = this.val(f, "tendenciaDia", "TendenciaDia");
      return t === undefined ? null : t;
    },
    rangoAutoDe(f) {
      return this.val(f, "rangoAutomatico", "RangoAutomatico");
    },
    rangoManualDe(f) {
      return this.val(f, "rangoManual", "RangoManual");
    },
    /** Abreviatura de la unidad de la variable ("" si el catálogo no la trae). */
    unidadDe(f) {
      return this.val(f, "unidad", "Unidad") || "";
    },

    /** Texto "min – max" de la banda normal o de advertencia de un rango. */
    textoRango(rango, banda) {
      if (!rango) return "—";
      if (banda === "normal") {
        return this.val(rango, "normalMin", "NormalMin") + " – " + this.val(rango, "normalMax", "NormalMax");
      }
      const minimo = this.val(rango, "advertenciaMin", "AdvertenciaMin");
      const maximo = this.val(rango, "advertenciaMax", "AdvertenciaMax");
      return (minimo === null || minimo === undefined ? "—" : minimo) + " – " +
             (maximo === null || maximo === undefined ? "—" : maximo);
    },

    /** Etiqueta legible del estado del renglón. */
    etiquetaEstado(estado) {
      const nombres = { NORMAL: "Normal", ADVERTENCIA: "Advertencia", ALARMA: "Alarma", SIN_SENAL: "Sin señal", SIN_RANGO: "Sin rango" };
      return nombres[estado] || estado || "—";
    },
    /** Color Vuetify del chip de estado. */
    colorEstado(estado) {
      const colores = { NORMAL: "green", ADVERTENCIA: "orange darken-1", ALARMA: "red darken-1", SIN_SENAL: "blue-grey", SIN_RANGO: "grey" };
      return colores[estado] || "grey";
    },
    /** Color del valor de la última lectura según el estado. */
    colorValor(estado) {
      if (estado === "ALARMA") return "#c62828";
      if (estado === "ADVERTENCIA") return "#b26a00";
      return "#1c2b2a";
    },

    /** Descarga el tablero. Con silencioso=true no parpadea el botón (refresco del timer). */
    async cargar(silencioso) {
      if (!silencioso) this.cargando = true;
      try {
        const r = await axios.get("/PredictivoAnomalias/Estado");
        this.tablero = r.data || {};
        this.error = "";
      } catch (e) {
        this.error = "No se pudo consultar el reporte: " + (e.message || e);
      } finally {
        this.cargando = false;
      }
    },

    /** Botón "Actualizar ahora": recalcula en el backend ignorando el caché. */
    async refrescar() {
      this.cargando = true;
      try {
        const r = await axios.post("/PredictivoAnomalias/Refrescar", {});
        this.tablero = r.data || {};
        this.error = "";
      } catch (e) {
        this.error = "No se pudo refrescar: " + (e.message || e);
      } finally {
        this.cargando = false;
      }
    },

    /** Cierra cualquier panel abierto. */
    cerrarPanel() {
      this.abierto = { id: null, tipo: null };
      this.confirmandoRecalculo = false;
    },

    /**
     * Abre (o cierra, si ya estaba abierta) la gráfica de un renglón:
     * 'pronostico' = histórico diario + proyección; 'hoy' = por minuto.
     */
    async abrirSerie(f, tipo) {
      const id = this.idDe(f);
      if (this.abierto.id === id && this.abierto.tipo === tipo) {
        this.cerrarPanel();
        return;
      }
      this.abierto = { id, tipo };
      this.cargandoSerie = true;
      try {
        const accion = tipo === "hoy" ? "SerieHoy" : "SeriePronostico";
        const r = await axios.get("/PredictivoAnomalias/" + accion + "/" + id);
        this.pintarGrafica(r.data || {}, tipo);
        this.error = "";
      } catch (e) {
        this.error = "No se pudo cargar la gráfica: " + (e.message || e);
        this.cerrarPanel();
      } finally {
        this.cargandoSerie = false;
      }
    },

    /** Convierte la respuesta de serie en opciones/series de ApexCharts. */
    pintarGrafica(datos, tipo) {
      // Umbrales del rango en uso como líneas punteadas sobre la gráfica.
      const rango = this.val(datos, "rango", "Rango");
      const anotaciones = [];
      const linea = (y, color, texto) => {
        if (y === null || y === undefined) return;
        anotaciones.push({
          y, borderColor: color, strokeDashArray: 6,
          label: { text: texto, style: { color: "#fff", background: color } },
        });
      };
      if (rango) {
        linea(this.val(rango, "normalMax", "NormalMax"), "#f9a825", "normal máx");
        linea(this.val(rango, "normalMin", "NormalMin"), "#f9a825", "normal mín");
        linea(this.val(rango, "advertenciaMax", "AdvertenciaMax"), "#c62828", "alarma");
        linea(this.val(rango, "advertenciaMin", "AdvertenciaMin"), "#c62828", "alarma");
      }

      const base = {
        chart: { animations: { enabled: false }, toolbar: { show: true } },
        yaxis: { decimalsInFloat: 2 },
        annotations: { yaxis: anotaciones },
        tooltip: { shared: true },
        legend: { show: true },
      };

      if (tipo === "hoy") {
        this.chartSeries = [{ name: "valor", data: this.val(datos, "valores", "Valores") || [] }];
        this.chartOptions = Object.assign({}, base, {
          stroke: { width: 2, curve: "straight" },
          colors: ["#1976d2"],
          xaxis: { categories: this.val(datos, "minutos", "Minutos") || [], tickAmount: 12, labels: { rotate: 0 } },
        });
      } else {
        this.chartSeries = [
          { name: "histórico (prom. diario)", data: this.val(datos, "historico", "Historico") || [] },
          { name: "proyección", data: this.val(datos, "proyeccion", "Proyeccion") || [] },
        ];
        this.chartOptions = Object.assign({}, base, {
          stroke: { width: [2.5, 2.5], curve: "straight", dashArray: [0, 6] },
          colors: ["#1976d2", "#197F72"],
          xaxis: { categories: this.val(datos, "etiquetas", "Etiquetas") || [], tickAmount: 10, labels: { rotate: 0 } },
        });
      }
      this.graficaKey++;
    },

    /** Abre (o cierra) el panel de rangos del renglón y precarga su modo actual. */
    abrirRangos(f) {
      const id = this.idDe(f);
      if (this.abierto.id === id && this.abierto.tipo === "rangos") {
        this.cerrarPanel();
        return;
      }
      this.abierto = { id, tipo: "rangos" };
      this.modoSel = this.modoDe(f);
      this.modoGuardado = false;
      this.confirmandoRecalculo = false;
    },

    /**
     * Botón "Recalcular" de la tarjeta del rango automático: el primer clic arma
     * la confirmación en el propio botón y el segundo la ejecuta. El backend
     * cierra el rango congelado vigente e inserta el nuevo con los últimos 15
     * días; con historial insuficiente avisa y no toca nada.
     */
    async recalcular(f) {
      if (!this.confirmandoRecalculo) {
        this.confirmandoRecalculo = true;
        return;
      }
      this.recalculando = true;
      try {
        await axios.post("/PredictivoAnomalias/RecalcularRango", {
          idMaquinaSensorVariable: this.idDe(f),
        });
        await this.cargar(true);
        this.error = "";
      } catch (e) {
        const cuerpo = e.response && e.response.data;
        this.error = "No se pudo recalcular el rango: " +
          ((cuerpo && (cuerpo.msg || cuerpo.Msg)) || e.message || e);
      } finally {
        this.recalculando = false;
        this.confirmandoRecalculo = false;
      }
    },

    /** Botón Aplicar del panel de rangos: guarda el modo y recarga el tablero. */
    async guardarModo(f) {
      this.guardandoModo = true;
      this.modoGuardado = false;
      try {
        await axios.post("/PredictivoAnomalias/CambiarModo", {
          idMaquinaSensorVariable: this.idDe(f),
          modo: this.modoSel,
        });
        await this.cargar(true);
        this.modoGuardado = true;
        this.error = "";
      } catch (e) {
        this.error = "No se pudo guardar el modo: " + (e.message || e);
      } finally {
        this.guardandoModo = false;
      }
    },
  },
  mounted() {
    // Primera carga y refresco cada minuto mientras la pestaña esté activa.
    this.cargar();
    this.timer = setInterval(() => {
      if (this.active) this.cargar(true);
    }, 60000);
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer);
  },
};
</script>

<style scoped>
/* Paleta del reporte: teal de marca para lo estable, ámbar/rojo para desviaciones. */
#predictivo-anomalias { background: #f5f7f7; padding: 12px; }
.pa-head { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; background: linear-gradient(90deg, #e1f5ee, #ffffff); border-radius: 12px; margin-bottom: 12px; }
.pa-head h1 { font-size: 22px; color: #0d564f; margin: 0; }
.pa-head .sub { color: #5f6f6c; font-size: 13px; }
.pa-head .der { display: flex; align-items: center; gap: 12px; }
.enlace { font-size: 12px; font-weight: 600; padding: 3px 10px; border-radius: 10px; }
.enlace.vivo { background: #e5f5e9; color: #1b8a5a; }
.enlace.caido { background: #fdecea; color: #c62828; }
.kpi { font-size: 12px; font-weight: 600; color: #b26a00; }
.claro { border: 1px solid #c9d4d2; background: #fff; border-radius: 8px; padding: 5px 12px; cursor: pointer; font-size: 13px; }
.th { background: #0d564f; color: #fff !important; white-space: nowrap; }
.centrado { text-align: center; }
.fw { font-weight: 700; }
.mini { color: #888; font-size: 12px; }
.mono { font-family: ui-monospace, Menlo, monospace; }
.acciones { white-space: nowrap; }
.accion { border-radius: 8px; padding: 4px 10px; margin: 0 2px; font-size: 12.5px; cursor: pointer; border: 1px solid; background: #fff; }
.accion.verde { color: #197f72; border-color: #197f72; }
.accion.azul { color: #1976d2; border-color: #1976d2; }
.accion.ambar { color: #b26a00; border-color: #b26a00; }
.accion.solido { background: #197f72; color: #fff; }
.accion.ambar.solido { background: #b26a00; color: #fff; }
.panel td { background: #fafcfc; }
.pa-panel { padding: 14px; }
.panel-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; color: #0d564f; }
.rangos { display: flex; gap: 12px; flex-wrap: wrap; }
.tarjeta { flex: 1 1 320px; border: 1px solid #d7e3e0; border-radius: 10px; padding: 12px 14px; background: #fff; }
.tarjeta.activa { border: 2px solid #197f72; background: #f2faf8; }
.tarjeta-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
.enuso { font-size: 10px; font-weight: 700; color: #fff; background: #197f72; border-radius: 8px; padding: 2px 8px; }
.enuso.ambar-bg { background: #b26a00; }
.ambar-txt { color: #b26a00; }
.modo-barra { display: flex; align-items: center; gap: 14px; margin-top: 12px; flex-wrap: wrap; }
.modo-barra label { font-size: 13px; cursor: pointer; }
.ok { color: #1b8a5a; font-weight: 700; font-size: 13px; }
.nota { margin-top: 8px; }
.error-msg { background: #fdecea; color: #c62828; border-radius: 8px; padding: 8px 12px; margin-bottom: 10px; font-size: 13px; }
</style>
